/**
 * AmendShareModal — Tier 0 slice-local component (SCRUM-172).
 *
 * Amends the current ownership's share percentage (1–100). Mirrors the
 * `UnitStatusModal` dialog contract (scrim + `role="dialog"` +
 * `useFocusTrap` + Esc + inline `role="alert"` + polite SR announce).
 * Client-side validation matches the BE `amendOwnershipShareSchema`
 * (`z.number().int().min(1).max(100)`); Save is disabled until valid.
 * Requires the ownership `version` for the If-Match header.
 */

import * as React from 'react';
import { Button, TextField } from '@sparta/ui-kit';
import { useFocusTrap } from '../../../shared/hooks/useFocusTrap.js';
import { useSrAnnouncement } from '../../../shared/hooks/useSrAnnouncement.js';
import { useAmendShare } from '../api/use-ownership-mutations.js';
import type { OwnershipView } from '../api/types.js';

export interface AmendShareModalProps {
  readonly propertyId: string;
  readonly ownership: OwnershipView;
  readonly onClose: () => void;
}

/** Valid per amendOwnershipShareSchema: integer 1–100. */
function parseShare(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= 100 ? n : null;
}

export function AmendShareModal({
  propertyId,
  ownership,
  onClose,
}: AmendShareModalProps): React.ReactElement {
  const [raw, setRaw] = React.useState(String(ownership.sharePercent));
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const { mutateAsync, status, errorMessage, reset } = useAmendShare();
  const sr = useSrAnnouncement();

  useFocusTrap(dialogRef, { initialFocusRef: cancelRef });

  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const parsed = parseShare(raw);
  const isValid = parsed !== null;
  const isUnchanged = parsed === ownership.sharePercent;

  const handleSave = async (): Promise<void> => {
    if (parsed === null) return;
    try {
      await mutateAsync({
        propertyId,
        ownershipId: ownership.id,
        version: ownership.version,
        body: { newShare: parsed },
      });
      sr.polite('Share updated');
      onClose();
    } catch {
      // errorMessage drives the inline role="alert"; modal stays open.
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'var(--bg-overlay)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {sr.regions}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="amend-share-title"
        aria-describedby="amend-share-subtitle"
        className="w-full max-w-md rounded-2xl p-6"
        style={{ background: 'var(--bg-surface)', boxShadow: 'var(--shadow-xl)' }}
      >
        <h2
          id="amend-share-title"
          className="m-0 text-lg font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Amend share — {ownership.ownerName}
        </h2>
        <p
          id="amend-share-subtitle"
          className="mt-1 mb-4 text-sm"
          style={{ color: 'var(--fg-secondary)' }}
        >
          Set the ownership share as a whole percentage from 1 to 100.
        </p>

        <TextField
          label="Share (%)"
          type="number"
          min={1}
          max={100}
          step={1}
          inputMode="numeric"
          value={raw}
          error={
            raw.length > 0 && !isValid
              ? 'Enter a whole number from 1 to 100.'
              : undefined
          }
          onChange={(e) => {
            if (errorMessage !== null) reset();
            setRaw(e.target.value);
          }}
        />

        {errorMessage !== null ? (
          <div
            role="alert"
            className="mt-4 rounded-lg p-3 text-sm"
            style={{
              background:
                'color-mix(in oklab, var(--danger-500) 12%, var(--bg-surface))',
              color: 'var(--fg-danger-on-tint)',
              border:
                '1px solid color-mix(in oklab, var(--danger-500) 30%, transparent)',
            }}
          >
            {errorMessage}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <Button ref={cancelRef} variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSave();
            }}
            disabled={!isValid || isUnchanged || status === 'pending'}
          >
            {status === 'pending' ? 'Saving…' : 'Save share'}
          </Button>
        </div>
      </div>
    </div>
  );
}
